import * as THREE from "three";
import { GraphicsProfile, GraphicsSettings, ShadowMode, effectivePixelRatio } from "./graphics_quality.js";

interface MaterialBackup
{
  normalMap: THREE.Texture | null;
}

type MaterialWithMaps = THREE.Material & { normalMap?: THREE.Texture | null; needsUpdate: boolean };

const material_backup = new WeakMap<THREE.Material, MaterialBackup>();
const receive_backup = new WeakMap<THREE.Object3D, boolean>();
const phong_backup = new WeakMap<THREE.Mesh, THREE.Material | THREE.Material[]>();
const lambert_variants = new WeakMap<THREE.Material, THREE.MeshLambertMaterial>();
const lambert_set = new WeakSet<THREE.Material>();

/**
 * Per-material opt-outs (`material.userData`), used by the columns:
 * `keep_normal_map` keeps the normal map when the profile drops normal maps,
 * `keep_specular` keeps a Phong material from becoming Lambert.
 */
function isSwappablePhong( material: THREE.Material | undefined ): boolean
{
  return !!material
    && !!( material as THREE.MeshPhongMaterial ).isMeshPhongMaterial
    && material.userData.keep_specular !== true;
}

function lambertFor( source: THREE.MeshPhongMaterial ): THREE.MeshLambertMaterial
{
  const cached = lambert_variants.get( source );
  if ( cached ) return cached;

  const variant = new THREE.MeshLambertMaterial();
  variant.copy( source as unknown as THREE.MeshLambertMaterial );
  variant.onBeforeCompile = source.onBeforeCompile;
  variant.customProgramCacheKey = source.customProgramCacheKey;
  variant.name = source.name ? `${source.name}_lambert` : "lambert_variant";
  lambert_variants.set( source, variant );
  lambert_set.add( variant );
  return variant;
}

function isLambertVariant( material: THREE.Material | THREE.Material[] ): boolean
{
  return Array.isArray( material )
    ? material.every( m => !m || lambert_set.has( m ) || !isSwappablePhong( m ) )
    : lambert_set.has( material );
}

function withoutSpecular( material: THREE.Material ): THREE.Material
{
  return isSwappablePhong( material )
    ? lambertFor( material as THREE.MeshPhongMaterial )
    : material;
}

export function shadowMapTypeFor( mode: ShadowMode ): THREE.ShadowMapType
{
  switch ( mode )
  {
    case "basic": return THREE.BasicShadowMap;
    case "pcf": return THREE.PCFShadowMap;
    case "pcf_soft":
    default: return THREE.PCFSoftShadowMap;
  }
}

export class GraphicsApply
{
  public static applyRenderer( renderer: THREE.WebGLRenderer, profile: GraphicsProfile = GraphicsSettings.profile() ): boolean
  {
    const shadows_on = profile.shadows !== "off";
    const type = shadowMapTypeFor( profile.shadows );
    let shadow_setup_changed = false;

    if ( renderer.shadowMap.enabled !== shadows_on )
    {
      renderer.shadowMap.enabled = shadows_on;
      shadow_setup_changed = true;
    }

    if ( renderer.shadowMap.type !== type )
    {
      renderer.shadowMap.type = type;
      shadow_setup_changed = true;
    }

    const wanted_ratio = effectivePixelRatio( profile );
    if ( Math.abs( renderer.getPixelRatio() - wanted_ratio ) > 1e-3 )
      renderer.setPixelRatio( wanted_ratio );

    renderer.shadowMap.needsUpdate = true;
    return shadow_setup_changed;
  }

  public static applyScene(
    world: THREE.Object3D,
    profile: GraphicsProfile = GraphicsSettings.profile(),
    refresh_materials = false
  ): void
  {
    const receive = profile.receive_shadows && profile.shadows !== "off";

    world.traverse( object =>
    {
      const light = object as THREE.Light & { isDirectionalLight?: boolean; isHemisphereLight?: boolean };

      if ( light.isDirectionalLight )
      {
        if ( object.name === "rim" )
          object.visible = profile.rim_light;
        this.applySunShadow( object as THREE.DirectionalLight, profile );
        return;
      }

      if ( light.isHemisphereLight )
      {
        if ( object.name !== "rim_substitute" )
          light.intensity = profile.hemisphere_intensity;
        return;
      }

      const mesh = object as THREE.Mesh;
      if ( !mesh.isMesh )
        return;

      this.applySpecular( mesh, profile );

      const materials = Array.isArray( mesh.material ) ? mesh.material : [ mesh.material ];
      let is_shadow_catcher = false;

      for ( const material of materials )
      {
        if ( !material ) continue;
        if ( ( material as THREE.ShadowMaterial ).isShadowMaterial )
          is_shadow_catcher = true;
        this.applyMaterial( material as MaterialWithMaps, profile, refresh_materials );
      }

      if ( is_shadow_catcher )
      {
        mesh.visible = profile.shadows !== "off";
        return;
      }

      if ( !receive_backup.has( mesh ) )
        receive_backup.set( mesh, mesh.receiveShadow );

      const original = receive_backup.get( mesh ) ?? false;
      const wanted = original && receive;
      if ( mesh.receiveShadow !== wanted )
      {
        mesh.receiveShadow = wanted;
        for ( const material of materials )
          if ( material ) material.needsUpdate = true;
      }
    } );
  }

  private static applySpecular( mesh: THREE.Mesh, profile: GraphicsProfile ): void
  {
    const current = mesh.material;

    if ( profile.specular )
    {
      const original = phong_backup.get( mesh );
      if ( original )
      {
        if ( isLambertVariant( current ) )
          mesh.material = original;
        phong_backup.delete( mesh );
      }
      return;
    }

    if ( phong_backup.has( mesh ) && isLambertVariant( current ) )
      return;

    const has_phong = Array.isArray( current )
      ? current.some( m => isSwappablePhong( m ) )
      : isSwappablePhong( current );
    if ( !has_phong )
      return;

    phong_backup.set( mesh, current );
    mesh.material = Array.isArray( current ) ? current.map( withoutSpecular ) : withoutSpecular( current );
  }

  private static applySunShadow( light: THREE.DirectionalLight, profile: GraphicsProfile ): void
  {
    if ( !light.castShadow )
      return;

    const size = profile.shadow_map_size;
    if ( light.shadow.mapSize.width !== size || light.shadow.mapSize.height !== size )
    {
      light.shadow.mapSize.set( size, size );
      light.shadow.map?.dispose();
      light.shadow.map = null as unknown as THREE.WebGLRenderTarget;
    }
  }

  private static applyMaterial( material: MaterialWithMaps, profile: GraphicsProfile, refresh: boolean ): void
  {
    let dirty = refresh;

    if ( "normalMap" in material )
    {
      if ( !material_backup.has( material ) )
        material_backup.set( material, { normalMap: material.normalMap ?? null } );

      const saved = material_backup.get( material )!;
      if ( profile.normal_maps && material.normalMap && saved.normalMap !== material.normalMap )
        saved.normalMap = material.normalMap;

      const wanted = profile.normal_maps || material.userData.keep_normal_map === true ? saved.normalMap : null;
      if ( ( material.normalMap ?? null ) !== wanted )
      {
        material.normalMap = wanted;
        dirty = true;
      }
    }

    if ( dirty )
      material.needsUpdate = true;
  }
}
